import type { Schema } from "@/shared/components/Form/types";

// Валюты контракта (см. backend CONTRACT_CURRENCIES): у банковской строки
// нет смысла заводить валюту, которой не бывает у контракта, поэтому поле
// выбирается из этих двух значений, а не свободным текстом.
const BANK_ACCOUNT_CURRENCY_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
];

const INVOICE_LANGUAGE_OPTIONS = [
  { label: "Английский", value: "en" },
  { label: "Русский", value: "ru" },
];

function getNewBankAccount() {
  return {
    currency: "USD",
    beneficiary: "",
    bankName: "",
    bankAddress: "",
    iban: "",
    swift: "",
    instructions: "",
  };
}

/**
 * Схема формы реквизитов продавца ottkit (спека §3). Строгих `required` на
 * реквизитах юрлица нет намеренно: форма сохраняется по частям, как и
 * реквизиты платформы (см. platformSettings), а не заполняется одним махом.
 * Предупреждение о незаполненности показывает вызывающий компонент по
 * `billingSeller.filled`.
 */
export function makeBillingSellerSchema(): Schema {
  return {
    companyName: {
      type: "text",
      label: "Юрлицо",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    registrationNumber: {
      type: "text",
      label: "Регистрационный номер",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    vatNumber: {
      type: "text",
      label: "Налоговый номер (VAT)",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    address: {
      type: "text",
      label: "Адрес",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    email: {
      type: "email",
      label: "Email",
      xs: 12,
      sm: 4,
      fullWidth: true,
    },
    invoiceNumberPrefix: {
      type: "text",
      label: "Префикс номера счёта",
      helperText: "Например, OTT: номера счетов выглядят как OTT-2026-001",
      xs: 12,
      sm: 4,
      fullWidth: true,
    },
    invoiceLanguage: {
      type: "select",
      label: "Язык счёта",
      xs: 12,
      sm: 4,
      fullWidth: true,
      options: INVOICE_LANGUAGE_OPTIONS,
    },
    vatNote: {
      type: "text",
      label: "Примечание по НДС",
      helperText:
        "Печатается на счёте рядом с суммой, например про reverse charge",
      xs: 12,
      fullWidth: true,
      multiline: true,
      rows: 2,
    },
    signerName: {
      type: "text",
      label: "Подписант, ФИО",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    signerPosition: {
      type: "text",
      label: "Подписант, должность",
      xs: 12,
      sm: 6,
      fullWidth: true,
    },
    logoId: {
      type: "image",
      imageType: "avatar",
      label: "Логотип на счёте",
      xs: 12,
      sm: 4,
    },
    brandColor: {
      type: "color",
      label: "Цвет бренда",
      xs: 12,
      sm: 4,
    },
    bankAccounts: {
      type: "array",
      label: "Банковские реквизиты",
      addText: "Добавить банковскую строку",
      xs: 12,
      getNewItem: getNewBankAccount,
      getTitle: ({ item }) =>
        item?.currency ? `Счёт в ${item.currency}` : "Новая банковская строка",
      subschema: {
        currency: {
          type: "select",
          label: "Валюта",
          xs: 12,
          sm: 3,
          fullWidth: true,
          required: true,
          options: BANK_ACCOUNT_CURRENCY_OPTIONS,
        },
        beneficiary: {
          type: "text",
          label: "Получатель",
          xs: 12,
          sm: 5,
          fullWidth: true,
        },
        bankName: {
          type: "text",
          label: "Банк",
          xs: 12,
          sm: 4,
          fullWidth: true,
        },
        bankAddress: {
          type: "text",
          label: "Адрес банка",
          xs: 12,
          sm: 6,
          fullWidth: true,
        },
        iban: {
          type: "text",
          label: "IBAN",
          xs: 12,
          sm: 3,
          fullWidth: true,
        },
        swift: {
          type: "text",
          label: "SWIFT",
          xs: 12,
          sm: 3,
          fullWidth: true,
        },
        instructions: {
          type: "text",
          label: "Инструкции по оплате",
          xs: 12,
          fullWidth: true,
          multiline: true,
          rows: 2,
        },
      },
    },
  };
}
